import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/supplier-engagement-batch-invite.js

import { trackCustomEvent } from '@/utils/tracking';

/**
 * Server-driven batch invite.
 *
 * Frontend POSTs validated rows to /supplier-engagement/.../batch-invite. The
 * backend persists a job record, a cron worker invites rows in parallel chunks
 * (SE_BATCH_CONCURRENCY, default 5), and streams progress via WebSocket
 * (batch-invite:progress / :completed / :failed). Each row on the backend runs
 * the full single-invite flow — Snowflake proc + getSupplierByLinkingId +
 * addSupplierToStrapi + cross-project notification — so suppliers persist to
 * Strapi as they're invited.
 *
 * Why server-driven: navigate-away + reload + re-login resumability. The
 * banner hydrates from /batch-invite/active on mount, so an in-flight batch
 * is always visible until the user dismisses it.
 *
 * State shape (store.state.data.supplierEngagementBatchInvite):
 *   {
 *     activeBatches: [{
 *       batchId, status: 'queued'|'processing'|'completed'|'failed',
 *       totalRows, processedRows, successCount, errorCount, errorMessage?,
 *       sourceFileName?
 *     }],
 *     dismissedBatchIds: Set<number>
 *   }
 */

const STATE_KEY = 'supplierEngagementBatchInvite';

// No mid-batch list refreshes. The backend persists each row as it invites
// (via `addSupplierToStrapi`), and the SCM page's existing stale-detection
// + visibility-change hooks pick up the new rows on natural interactions.
// Explicit populates during a 1 500-row batch are ~4-8s each and just spam
// Snowflake (`[SCM Populate] Found N suppliers` in the logs). We refresh
// once on completion so the final state is guaranteed fresh.

export class SupplierEngagementBatchInviteHandler {
  constructor(store) {
    this.store = store;
    this.registered = false;

    if (!store.state.data[STATE_KEY]) {
      store.state.data[STATE_KEY] = {
        activeBatches: [],
        dismissedBatchIds: new Set()
      };
    }
  }

  registerWebSocketHandlers() {
    if (this.registered) return;
    const ws = this.store.webSocketHandler;
    if (!ws?.registerHandler) return;

    ws.registerHandler(
      'seBatchInviteProgress',
      (event) => this._applyEvent(event),
      ['batch-invite:progress', 'batch-invite:completed', 'batch-invite:failed']
    );

    this.registered = true;
  }

  _applyEvent(event) {
    const type = event?.type || event?.event;
    const data = event?.data || event;
    if (!data?.batchId) return;

    const current = this.store.state.data[STATE_KEY];
    if (current.dismissedBatchIds.has(data.batchId)) return;

    const existing = current.activeBatches.find(b => b.batchId === data.batchId);
    const others = current.activeBatches.filter(b => b.batchId !== data.batchId);

    if (type === 'batch-invite:progress') {
      this._updateState({
        activeBatches: [
          ...others,
          { ...(existing || {}), ...data, status: 'processing' }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });
      // Intentionally NO supplier list refresh here — see comment at top.
      return;
    }

    if (type === 'batch-invite:completed') {
      const totalRows = data.totalRows ?? existing?.totalRows;
      this._updateState({
        activeBatches: [
          ...others,
          {
            ...(existing || {}),
            batchId: data.batchId,
            status: 'completed',
            totalRows,
            processedRows: totalRows,
            successCount: data.successCount ?? existing?.successCount ?? 0,
            errorCount: data.errorCount ?? existing?.errorCount ?? 0
          }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });

      // Single force-refresh on completion so the SCM list reflects every
      // row we just invited in one guaranteed-fresh swoop.
      this.store.supplychainSuppliersHandler?.loadSupplychainSuppliers?.(true);

      trackCustomEvent('supplier_batch_invite_complete', {
        batch_id: data.batchId,
        supplier_count: totalRows,
        success_count: data.successCount ?? 0,
        error_count: data.errorCount ?? 0
      });
      return;
    }

    if (type === 'batch-invite:failed') {
      this._updateState({
        activeBatches: [
          ...others,
          {
            ...(existing || {}),
            batchId: data.batchId,
            status: 'failed',
            errorMessage: data.error || 'Batch failed'
          }
        ],
        dismissedBatchIds: current.dismissedBatchIds
      });
    }
  }

  _updateState(next) {
    this.store.state.data[STATE_KEY] = next;
    this.store.notifySubscribers?.(STATE_KEY, next);
  }

/**
   * Enqueue a batch on the backend. Returns as soon as the job record is
   * created — the cron worker drives the actual invites, reported via WS.
   *
   * `templateId` (optional, Phase 1 spec §01) is the invitation template the
   * user picked in the batch dialog. Backend validates it server-side and
   * dispatches it per row alongside the legacy SP email. Null = legacy
   * behaviour (SP email only).
   */
  async submitBatch({ sourceFileName, rows, templateId = null }) {
    const projectId = this.store.getProjectId?.();
    if (!projectId) throw new Error('No project ID available');
    if (!Array.isArray(rows) || rows.length === 0) {
      throw new Error('No rows to invite');
    }

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-invite`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStrapiToken()}`
        },
        body: JSON.stringify({ sourceFileName, rows, templateId })
      }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || err.message || `Batch enqueue failed (${response.status})`);
    }

    const body = await response.json();
    const current = this.store.state.data[STATE_KEY];
    this._updateState({
      activeBatches: [
        ...current.activeBatches,
        {
          batchId: body.batchId,
          sourceFileName: sourceFileName || null,
          status: body.status || 'queued',
          totalRows: body.totalRows,
          processedRows: 0,
          successCount: 0,
          errorCount: 0
        }
      ],
      dismissedBatchIds: current.dismissedBatchIds
    });

    return body;
  }

  /**
   * Hydrate the banner on page mount with any queued/processing batches,
   * so an in-flight job shows up after reload / re-login / navigate back.
   */
  async fetchActive() {
    const projectId = this.store.getProjectId?.();
    if (!projectId) return [];

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-invite/active`,
      {
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      }
    );

    if (!response.ok) return [];
    const batches = await response.json();

    const current = this.store.state.data[STATE_KEY];
    const notDismissed = Array.isArray(batches)
      ? batches.filter(b => !current.dismissedBatchIds.has(b.batchId))
      : [];

    this._updateState({
      activeBatches: notDismissed,
      dismissedBatchIds: current.dismissedBatchIds
    });

    return batches;
  }

  async fetchBatch(batchId) {
    const projectId = this.store.getProjectId?.();
    if (!projectId || !batchId) return null;

    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/supplier-engagement/project/${projectId}/batch-invite/${batchId}`,
      {
        headers: { Authorization: `Bearer ${getStrapiToken()}` }
      }
    );
    if (!response.ok) return null;
    return response.json();
  }

  dismissBatch(batchId) {
    const current = this.store.state.data[STATE_KEY];
    const dismissed = new Set(current.dismissedBatchIds);
    dismissed.add(batchId);

    this._updateState({
      activeBatches: current.activeBatches.filter(b => b.batchId !== batchId),
      dismissedBatchIds: dismissed
    });
  }
}

export const SUPPLIER_ENGAGEMENT_BATCH_STATE_KEY = STATE_KEY;
